/**
 * Phase 8 T-14 §13 E — Career page editor (Wrangle `260.png`–`280.png`).
 *
 * Left column: editor form
 *   - Header image upload (drag-drop zone, Replace / Remove, aspect hint)
 *   - Primary + Secondary colour hex inputs with native <input type="color"> swatch
 *   - Tagline text input
 *   - Save branding button → optimistic UPSERT, invalidates ['careers','brand',orgId]
 *
 * Right column: live preview card (max-w-[560px])
 *   - Logo · org name · tagline · published jobs list
 *
 * Bottom: Jobs section
 *   - Published / Archived / Draft status chips
 *   - Archive + Delete row actions
 *   - Create Job CTA
 *
 * Data:
 *   - Org name via `orgService.updateOrg`.
 *   - Branding (colours / tagline / logo path) via `careersBrandService` —
 *     replaces the old `career_primary_*`, `career_secondary_*`,
 *     `career_tagline_*` localStorage keys (C-33).
 *   - Logo upload to `ggj_b2b-candidate-resumes` bucket at
 *     `org-logos/<orgId>/logo.<ext>`; storage path persists in
 *     `ggj_b2b_careers_brand.logo_url` and is resolved to a signed URL on
 *     load.
 *   - Job list via `jobService.list`.
 */

import { useCallback, useEffect, useRef, useState, DragEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Archive,
  Building2,
  CheckCircle2,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@core/auth/useAuth';
import { orgService } from '@core/supabase/services/orgService';
import { jobService } from '@core/supabase/services/jobService';
import {
  careersBrandService,
  type CareersBrand,
} from '@core/supabase/services/careersBrandService';
import { queryKeys } from '@core/query/queryKeys';
import { supabase } from '@core/supabase/client';
import { B2B_CANDIDATE_RESUME_BUCKET } from '@core/supabase/storageConfig';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { useToast } from '@shared/hooks/useToast';
import type { Job, JobPublishStatus, Organization } from '@core/types';

/* ── helpers ── */
const DEFAULT_PRIMARY = '#8B80F9';
const DEFAULT_SECONDARY = '#22c55e';

function isValidHex(s: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(s);
}

/** Brand rows store storage paths (e.g. `org-logos/<orgId>/logo.png`). */
function isStoragePath(v: string): boolean {
  return v.startsWith('org-logos/');
}

async function resolveLogoUrl(pathOrUrl: string | null): Promise<string | null> {
  if (!pathOrUrl) return null;
  if (!isStoragePath(pathOrUrl)) return pathOrUrl;
  const { data } = await supabase.storage
    .from(B2B_CANDIDATE_RESUME_BUCKET)
    .createSignedUrl(pathOrUrl, 3600);
  return data?.signedUrl ?? null;
}

const JOB_PUBLISH_CHIP: Record<
  JobPublishStatus | 'open' | 'closed' | 'on_hold' | 'filled',
  { label: string; cls: string }
> = {
  published:      { label: 'Published',  cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  paused:         { label: 'Paused',     cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  draft:          { label: 'Draft',      cls: 'bg-slate-400/10 text-slate-500 dark:text-slate-400' },
  pending_review: { label: 'In review',  cls: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  scheduled:      { label: 'Scheduled',  cls: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
  closed:         { label: 'Closed',     cls: 'bg-slate-400/10 text-slate-500 dark:text-slate-400' },
  open:           { label: 'Open',       cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  on_hold:        { label: 'On hold',    cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  filled:         { label: 'Filled',     cls: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
};

function statusChipFor(job: Job): { label: string; cls: string } {
  const ps = job.publish_status;
  if (ps && JOB_PUBLISH_CHIP[ps]) return JOB_PUBLISH_CHIP[ps];
  return JOB_PUBLISH_CHIP[job.status] ?? { label: job.status, cls: 'bg-slate-400/10 text-slate-500' };
}

/* ── sub-components ── */

function ColorInput({
  label,
  hex,
  onChange,
}: {
  label: string;
  hex: string;
  onChange: (h: string) => void;
}) {
  const [raw, setRaw] = useState(hex);

  useEffect(() => { setRaw(hex); }, [hex]);

  const commit = () => {
    const v = raw.startsWith('#') ? raw : `#${raw}`;
    if (isValidHex(v)) onChange(v);
    else setRaw(hex);
  };

  return (
    <div className="flex-1 min-w-[140px]">
      <label className="text-[11px] vscode-text-tertiary mb-1 block">{label}</label>
      <div className="flex items-center gap-2 h-8 rounded-[8px] border border-[var(--vscode-border-primary)] px-2 bg-[var(--vscode-bg-primary)] overflow-hidden">
        {/* native colour swatch */}
        <label className="relative w-5 h-5 shrink-0 rounded-[4px] overflow-hidden cursor-pointer border border-[var(--vscode-border-primary)]"
          style={{ background: isValidHex(hex) ? hex : '#000000' }}>
          <input
            type="color"
            value={isValidHex(hex) ? hex : '#000000'}
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
            onChange={(e) => { setRaw(e.target.value); onChange(e.target.value); }}
          />
        </label>
        <input
          type="text"
          className="flex-1 bg-transparent text-[13px] vscode-text-primary outline-none min-w-0"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === 'Enter' && commit()}
          maxLength={7}
          placeholder="#000000"
        />
      </div>
    </div>
  );
}

function ImageDropZone({
  previewUrl,
  uploading,
  onFile,
  onRemove,
}: {
  previewUrl: string | null;
  uploading: boolean;
  onFile: (f: File) => void;
  onRemove: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) onFile(file);
  };

  return (
    <div>
      <label className="text-[11px] vscode-text-tertiary mb-1 block">
        Header image / logo
        <span className="ml-1 vscode-text-tertiary">(recommended 1200 × 400 px, 16:9 or wider)</span>
      </label>
      {previewUrl ? (
        <div className="relative rounded-[10px] overflow-hidden border border-[var(--vscode-border-primary)] h-[120px] bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_50%,transparent)]">
          <img src={previewUrl} alt="Header preview" className="w-full h-full object-cover" />
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/30 opacity-0 hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="text-xs rounded-[8px] bg-white/90 text-gray-900 px-3 py-1.5 font-medium hover:bg-white"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="text-xs rounded-[8px] bg-red-500/90 text-white px-3 py-1.5 font-medium hover:bg-red-500"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          className={[
            'rounded-[10px] border-2 border-dashed h-[120px] flex flex-col items-center justify-center gap-2',
            'cursor-pointer transition-colors text-center',
            drag
              ? 'border-emerald-500 bg-emerald-500/[0.06]'
              : 'border-[var(--vscode-border-primary)] hover:border-[color-mix(in_srgb,var(--vscode-primary)_50%,transparent)] hover:bg-[color-mix(in_srgb,var(--vscode-bg-hover)_50%,transparent)]',
          ].join(' ')}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        >
          {uploading ? (
            <Loader2 className="w-5 h-5 vscode-text-tertiary animate-spin" />
          ) : (
            <>
              <ImageIcon className="w-5 h-5 vscode-text-tertiary" />
              <p className="text-[12px] vscode-text-secondary">
                Drag and drop or <span className="text-[color:var(--vscode-text-link)]">browse</span>
              </p>
              <p className="text-[11px] vscode-text-tertiary">PNG, JPG, WebP · max 5 MB</p>
            </>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
      />
    </div>
  );
}

/* ── Live Preview Card ── */
function LivePreview({
  orgName,
  tagline,
  primaryColor,
  logoUrl,
  jobs,
}: {
  orgName: string;
  tagline: string;
  primaryColor: string;
  logoUrl: string | null;
  jobs: Job[];
}) {
  const published = jobs.filter(
    (j) => j.publish_status === 'published' || (!j.publish_status && j.status === 'open'),
  );

  return (
    <div
      className="rounded-[12px] border border-[var(--vscode-border-primary)] overflow-hidden bg-white dark:bg-[#181818] shadow-sm"
      style={{ maxWidth: 560 }}
    >
      {/* banner */}
      <div
        className="h-[80px] flex items-center px-5 gap-3"
        style={{ background: isValidHex(primaryColor) ? primaryColor : DEFAULT_PRIMARY }}
      >
        {logoUrl ? (
          <img src={logoUrl} alt="logo" className="h-10 w-10 rounded-full object-cover bg-white/20" />
        ) : (
          <div className="h-10 w-10 rounded-full bg-white/30 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
        )}
        <div>
          <p className="text-white font-bold text-[15px] leading-tight">
            {orgName || 'Your Organisation'}
          </p>
          {tagline && (
            <p className="text-white/80 text-[12px] leading-tight mt-0.5">{tagline}</p>
          )}
        </div>
      </div>

      {/* job list */}
      <div className="px-5 py-4">
        <p className="text-[12px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">
          Open positions
        </p>
        {published.length === 0 ? (
          <p className="text-[13px] text-gray-400 dark:text-gray-500 text-center py-4">
            No published jobs yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {published.slice(0, 5).map((j) => (
              <li
                key={j.id}
                className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0"
              >
                <div>
                  <p className="text-[13px] font-medium text-gray-900 dark:text-gray-100 leading-tight">{j.title}</p>
                  {j.location && (
                    <p className="text-[11px] text-gray-400 dark:text-gray-500">{j.location}</p>
                  )}
                </div>
                <span
                  className="shrink-0 text-[11px] font-medium rounded-full px-2 py-0.5"
                  style={{
                    background: isValidHex(primaryColor) ? `${primaryColor}18` : `${DEFAULT_PRIMARY}18`,
                    color: isValidHex(primaryColor) ? primaryColor : DEFAULT_PRIMARY,
                  }}
                >
                  Apply
                </span>
              </li>
            ))}
            {published.length > 5 && (
              <li className="text-center text-[12px] text-gray-400 dark:text-gray-500 pt-1">
                +{published.length - 5} more positions
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ── Jobs table row ── */
function JobRow({
  job,
  onArchive,
  onDelete,
}: {
  job: Job;
  onArchive: () => void;
  onDelete: () => void;
}) {
  const chip = statusChipFor(job);
  return (
    <div className="grid grid-cols-[1fr_120px_80px] gap-3 px-4 py-3 items-center text-[13px] border-b border-[var(--vscode-border-primary)] last:border-0 hover:bg-[color-mix(in_srgb,var(--vscode-bg-hover)_50%,transparent)] transition-colors">
      <div className="min-w-0">
        <Link
          to={`/jobs/${job.id}`}
          className="font-medium vscode-text-primary hover:text-[color:var(--vscode-text-link)] truncate block"
        >
          {job.title}
        </Link>
        {job.location && <p className="text-[11px] vscode-text-tertiary truncate">{job.location}</p>}
      </div>
      <span
        className={`inline-flex items-center w-fit rounded-full px-2 py-0.5 text-[11px] font-medium ${chip.cls}`}
      >
        {chip.label}
      </span>
      <div className="flex items-center gap-1 justify-end">
        <button
          type="button"
          title="Archive"
          onClick={onArchive}
          className="p-1.5 rounded-[6px] vscode-text-tertiary hover:vscode-text-primary hover:vscode-bg-hover transition-colors"
        >
          <Archive className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          title="Delete"
          onClick={onDelete}
          className="p-1.5 rounded-[6px] text-red-400 hover:text-red-500 hover:bg-red-50/60 dark:hover:bg-red-950/30 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/* ── Main page ─────────────────────────────────────────────────────────── */
interface BrandFormInput {
  primaryColor: string;
  accentColor: string;
  tagline: string;
  logoUrl: string | null;
}

export function CareerPageSettingsPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { toast, showToast } = useToast();
  const queryClient = useQueryClient();

  /* org name lives separately in ggj_b2b_organizations via orgService */
  const [org, setOrg] = useState<Organization | null>(null);
  const [orgLoading, setOrgLoading] = useState(true);

  /* editable local copies — seeded from the server row via useEffect */
  const [tagline, setTagline] = useState('');
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY);
  const [logoSignedUrl, setLogoSignedUrl] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);

  /* jobs state (unchanged) */
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [deletingJob, setDeletingJob] = useState<Job | null>(null);
  const [archivingJob, setArchivingJob] = useState<Job | null>(null);

  // ── branding query ─────────────────────────────────────────────────────
  const brandQuery = useQuery<CareersBrand>({
    queryKey: orgId ? queryKeys.careers.brand(orgId) : ['careers', 'brand', 'anon'],
    queryFn: () => careersBrandService.get(orgId),
    enabled: Boolean(orgId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Seed form state from the server response once it arrives; respect any
  // pending edits by only syncing when `updatedAt` changes (i.e. after save
  // or the first load).
  const serverUpdatedAt = brandQuery.data?.updatedAt ?? null;
  useEffect(() => {
    const brand = brandQuery.data;
    if (!brand) return;
    setTagline(brand.tagline ?? '');
    setPrimaryColor(brand.primaryColor ?? DEFAULT_PRIMARY);
    setSecondaryColor(brand.accentColor ?? DEFAULT_SECONDARY);
    void resolveLogoUrl(brand.logoUrl).then((url) => setLogoSignedUrl(url));
  }, [brandQuery.data, serverUpdatedAt]);

  // ── branding mutation (optimistic upsert) ──────────────────────────────
  const saveMutation = useMutation<
    CareersBrand,
    Error,
    BrandFormInput,
    { previous: CareersBrand | undefined }
  >({
    mutationFn: (form) =>
      careersBrandService.save(orgId, {
        primaryColor: form.primaryColor,
        accentColor: form.accentColor,
        tagline: form.tagline,
        logoUrl: form.logoUrl,
      }),
    onMutate: async (form) => {
      if (!orgId) return { previous: undefined };
      const key = queryKeys.careers.brand(orgId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CareersBrand>(key);
      queryClient.setQueryData<CareersBrand>(key, (prev) => ({
        orgId,
        primaryColor: form.primaryColor,
        accentColor: form.accentColor,
        tagline: form.tagline,
        logoUrl: form.logoUrl,
        updatedAt: prev?.updatedAt ?? null,
      }));
      return { previous };
    },
    onError: (err, _form, context) => {
      if (orgId && context?.previous) {
        queryClient.setQueryData(queryKeys.careers.brand(orgId), context.previous);
      }
      showToast({
        title: 'Save failed',
        body: err instanceof Error ? err.message : 'Could not save branding.',
        kind: 'error',
      });
    },
    onSuccess: (persisted) => {
      if (!orgId) return;
      // Write the authoritative row back so LivePreview + any other consumer
      // of the key refresh in lockstep.
      queryClient.setQueryData(queryKeys.careers.brand(orgId), persisted);
      void queryClient.invalidateQueries({ queryKey: queryKeys.careers.brand(orgId) });
      showToast({ title: 'Branding saved', body: 'Career page settings have been applied.' });
    },
  });

  // ── org + jobs load (unchanged from pre-C-33) ──────────────────────────
  useEffect(() => {
    if (!orgId) return;
    orgService.getOrg(orgId)
      .then((o) => setOrg(o))
      .catch(() => setOrg(null))
      .finally(() => setOrgLoading(false));

    jobService.list(orgId).then((res) => {
      setJobs(Array.isArray(res) ? res : (res as { data: Job[] }).data ?? []);
    }).catch(() => setJobs([])).finally(() => setJobsLoading(false));
  }, [orgId]);

  /* logo upload — persist the storage path on the brand row so it survives sessions */
  const handleLogoFile = useCallback(async (file: File) => {
    if (!orgId) return;
    setLogoUploading(true);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'png';
      const path = `org-logos/${orgId}/logo.${ext}`;
      const { error } = await supabase.storage
        .from(B2B_CANDIDATE_RESUME_BUCKET)
        .upload(path, file, { upsert: true, contentType: file.type });
      if (error) throw error;

      // Show the fresh upload immediately — don't wait for the brand-row
      // round-trip before the drop zone refreshes.
      const { data: signed } = await supabase.storage
        .from(B2B_CANDIDATE_RESUME_BUCKET)
        .createSignedUrl(path, 3600);
      setLogoSignedUrl(signed?.signedUrl ?? null);

      await careersBrandService.save(orgId, { logoUrl: path });
      await queryClient.invalidateQueries({ queryKey: queryKeys.careers.brand(orgId) });
      showToast({ title: 'Logo uploaded', body: 'Your header image has been updated.' });
    } catch (err) {
      showToast({
        title: 'Upload failed',
        body: err instanceof Error ? err.message : 'Could not upload image.',
        kind: 'error',
      });
    } finally {
      setLogoUploading(false);
    }
  }, [orgId, queryClient, showToast]);

  const handleLogoRemove = useCallback(async () => {
    if (!orgId) return;
    setLogoSignedUrl(null);
    try {
      await careersBrandService.save(orgId, { logoUrl: null });
      await queryClient.invalidateQueries({ queryKey: queryKeys.careers.brand(orgId) });
    } catch (err) {
      showToast({
        title: 'Could not detach logo',
        body: err instanceof Error ? err.message : 'Please try again.',
        kind: 'error',
      });
    }
    // Storage cleanup deferred — a later ticket can call storage.remove on the path.
  }, [orgId, queryClient, showToast]);

  /* save branding (colours + tagline; logo is saved inline on upload) */
  const handleSave = async () => {
    if (!orgId || !org) return;
    try {
      // Org name lives on `ggj_b2b_organizations`, not on the brand row.
      await orgService.updateOrg(orgId, { name: org.name });
      await saveMutation.mutateAsync({
        primaryColor,
        accentColor: secondaryColor,
        tagline,
        logoUrl: brandQuery.data?.logoUrl ?? null,
      });
    } catch {
      // onError handler already surfaced the toast for the brand mutation;
      // if the org-name update itself failed, surface that too.
      if (!saveMutation.isError) {
        showToast({
          title: 'Save failed',
          body: 'Could not update organisation name.',
          kind: 'error',
        });
      }
    }
  };

  /* archive job */
  const handleArchive = async (job: Job) => {
    setArchivingJob(null);
    try {
      await jobService.updateStatus(job.id, 'closed');
      setJobs((prev) => prev.map((j) => j.id === job.id ? { ...j, status: 'closed' as const } : j));
      showToast({ title: 'Job archived', body: `"${job.title}" has been closed.` });
    } catch (err) {
      showToast({ title: 'Archive failed', body: err instanceof Error ? err.message : 'Could not archive job.', kind: 'error' });
    }
  };

  /* delete job */
  const handleDelete = async (job: Job) => {
    setDeletingJob(null);
    try {
      await jobService.delete(job.id);
      setJobs((prev) => prev.filter((j) => j.id !== job.id));
      showToast({ title: 'Job deleted', body: `"${job.title}" has been removed.` });
    } catch (err) {
      showToast({ title: 'Delete failed', body: err instanceof Error ? err.message : 'Could not delete job.', kind: 'error' });
    }
  };

  const saving = saveMutation.isPending;
  const firstLoad = orgLoading || (Boolean(orgId) && brandQuery.isLoading);
  if (firstLoad) return <FullPageSpinner />;

  return (
    <div className="space-y-8 pb-10">
      {/* Toast portal */}
      {toast}

      {/* Section header */}
      <div>
        <h2 className="text-[15px] font-semibold">Career page</h2>
        <p className="text-[12px] vscode-text-tertiary mt-0.5">
          Customise the public-facing career page that candidates see when browsing your open roles.
          {/* Public URL surface is shipped separately — intentionally not linked from this settings panel. */}
        </p>
      </div>

      {/* ── Two-column editor / preview ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">

        {/* LEFT — editor form */}
        <div className="space-y-5">

          {/* Header image */}
          <ImageDropZone
            previewUrl={logoSignedUrl}
            uploading={logoUploading}
            onFile={handleLogoFile}
            onRemove={() => void handleLogoRemove()}
          />

          {/* Org name + tagline */}
          <div className="space-y-3">
            <div>
              <label className="text-[11px] vscode-text-tertiary mb-1 block">Organisation name</label>
              <input
                className="input w-full h-8 text-[13px]"
                value={org?.name ?? ''}
                onChange={(e) => setOrg((o) => o ? { ...o, name: e.target.value } : o)}
              />
            </div>
            <div>
              <label className="text-[11px] vscode-text-tertiary mb-1 block">
                Tagline
                <span className="ml-1 vscode-text-tertiary">(shown below the org name)</span>
              </label>
              <input
                className="input w-full h-8 text-[13px]"
                placeholder="e.g. Find your next great hire"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
              />
            </div>
          </div>

          {/* Colour inputs */}
          <div>
            <label className="text-[11px] vscode-text-tertiary mb-2 block">Brand colours</label>
            <div className="flex gap-3 flex-wrap">
              <ColorInput
                label="Primary colour"
                hex={primaryColor}
                onChange={setPrimaryColor}
              />
              <ColorInput
                label="Secondary colour"
                hex={secondaryColor}
                onChange={setSecondaryColor}
              />
            </div>
            <p className="text-[11px] vscode-text-tertiary mt-1.5">
              Colours persist to your org on save and apply to the public career page.
            </p>
          </div>

          {/* Save */}
          <div className="pt-1">
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleSave()}
              className="btn-primary flex items-center gap-2 h-9 px-5 text-[13px]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save branding'}
            </button>
          </div>
        </div>

        {/* RIGHT — live preview */}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary mb-3">
            Live preview
          </p>
          <LivePreview
            orgName={org?.name ?? ''}
            tagline={tagline}
            primaryColor={primaryColor}
            logoUrl={logoSignedUrl}
            jobs={jobs}
          />
        </div>
      </div>

      {/* ── Jobs section ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-[14px] font-semibold">Jobs</h3>
            <p className="text-[12px] vscode-text-tertiary mt-0.5">
              Manage which roles appear on your career page.
            </p>
          </div>
          <Link
            to="/jobs"
            className="btn-primary flex items-center gap-1.5 h-8 px-4 text-[13px]"
          >
            <Plus className="w-3.5 h-3.5" />
            Manage jobs
            <ExternalLink className="w-3 h-3 opacity-60" />
          </Link>
        </div>

        {jobsLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin vscode-text-tertiary" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="rounded-[10px] border border-[var(--vscode-border-primary)] px-4 py-8 text-center">
            <p className="text-[13px] vscode-text-secondary">No jobs yet.</p>
            <Link to="/jobs" className="mt-2 inline-flex items-center gap-1.5 text-[13px] text-[color:var(--vscode-text-link)] hover:underline">
              <Plus className="w-3.5 h-3.5" /> Create your first job
            </Link>
          </div>
        ) : (
          <div className="rounded-[10px] border border-[var(--vscode-border-primary)] overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-[1fr_120px_80px] gap-3 px-4 py-2.5 border-b border-[var(--vscode-border-primary)] bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_40%,transparent)]">
              <span className="text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary">Job</span>
              <span className="text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary">Status</span>
              <span />
            </div>
            {jobs.map((j) => (
              <JobRow
                key={j.id}
                job={j}
                onArchive={() => setArchivingJob(j)}
                onDelete={() => setDeletingJob(j)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Confirm: archive */}
      <ConfirmDialog
        open={!!archivingJob}
        onClose={() => setArchivingJob(null)}
        onConfirm={() => archivingJob && void handleArchive(archivingJob)}
        title="Archive job?"
        message={`Close "${archivingJob?.title ?? 'this job'}"? It will no longer appear as open on your career page.`}
        confirmLabel="Archive"
        variant="primary"
      />

      {/* Confirm: delete */}
      <ConfirmDialog
        open={!!deletingJob}
        onClose={() => setDeletingJob(null)}
        onConfirm={() => deletingJob && void handleDelete(deletingJob)}
        title="Delete job?"
        subtitle="This action cannot be undone"
        message={`Permanently delete "${deletingJob?.title ?? 'this job'}" and all its applications?`}
        confirmLabel="Delete"
        variant="danger"
        requireTypedConfirm={deletingJob?.title}
      />
    </div>
  );
}
